import { AddServiceAreaButton } from './AddServiceAreaButton';
import { AddMissingSkillButton } from './AddMissingSkillButton';
import { Collapse, CollapseInline, COLLAPSE_MS, useDepartingRows } from './collapse';
import { useCallback, useMemo } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { cn } from '@/lib/utils';
import {
  groupConflicts,
  SEVERITY_DOT_CLASSES,
  type ScheduleConflict,
} from '@/lib/scheduler/scheduleConflicts';

export interface ConflictListTechnician {
  id: string;
  name: string;
}

export interface ConflictListProps {
  conflicts: ScheduleConflict[];
  /** Names for the per-technician headers (only shown when >1 technician is involved). */
  technicians?: ConflictListTechnician[];
  compact?: boolean;
  allowTechnicianUpdates?: boolean;
  className?: string;
  /**
   * Given by the read-side "acknowledged issues" note only, where the list is a
   * saved record rather than a live check. Each row then offers a dismiss that
   * takes that objection off the visit for good — the one answer available for
   * an objection about a moment that has already passed ("booked outside
   * working hours"), which no data fix can make untrue.
   *
   * A row identifies itself rather than naming a type: two crew members who are
   * both double-booked raise the same type twice, and dismissing one must not
   * put away the other.
   */
  onDismiss?: (issues: DismissedIssue[]) => void | Promise<unknown>;
}

/** What a dismissed row sends: enough to tell it from its neighbours. */
export interface DismissedIssue {
  type: string;
  message: string;
  technicianId?: string;
}

const dismissedIssue = (conflict: ScheduleConflict): DismissedIssue => ({
  type: conflict.type,
  message: conflict.message,
  ...(conflict.technicianId ? { technicianId: conflict.technicianId } : {}),
});

/** The combined business-hours + technician-hours line renders as one row. */
const HOURS_ROW_KEY = '__hours__';

/** The per-row dismiss: quiet until the row is hovered or focused. */
function DismissIssueButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={label}
      title={label}
      className="ml-auto size-6 shrink-0 self-start p-0 text-muted-foreground opacity-60 transition-opacity hover:bg-transparent hover:text-foreground hover:opacity-100 focus-visible:opacity-100"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <X aria-hidden="true" className="size-3.5" />
    </Button>
  );
}

export function OverlappingAppointments({ conflict }: { conflict: ScheduleConflict }) {
  const appointments = conflict.details?.appointments;
  if (conflict.type !== 'TECH_DOUBLE_BOOKED' || !Array.isArray(appointments)) return null;
  return (
    <span className="mt-1 block space-y-1">
      {appointments.map((raw: unknown) => {
        if (!raw || typeof raw !== 'object') return null;
        const appointment = raw as Record<string, unknown>;
        if (typeof appointment.id !== 'string') return null;
        const title =
          [
            appointment.customerName,
            appointment.reference,
            typeof appointment.visitNumber === 'number'
              ? `Visit #${appointment.visitNumber}`
              : null,
          ]
            .filter((value): value is string => typeof value === 'string' && value.length > 0)
            .join(' · ') || 'Overlapping appointment';
        let time = '';
        if (typeof appointment.startAt === 'string' && typeof appointment.endAt === 'string') {
          try {
            const formatter = new Intl.DateTimeFormat('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              timeZoneName: 'short',
              timeZone: typeof appointment.timezone === 'string' ? appointment.timezone : 'UTC',
            });
            time = formatter.formatRange(
              new Date(appointment.startAt),
              new Date(appointment.endAt),
            );
          } catch {
            /* Older saved conflicts may not have valid time bounds. */
          }
        }
        return (
          <span key={appointment.id} className="block rounded border border-current/20 px-2 py-1.5">
            <span className="block font-medium">{title}</span>
            {time ? <span className="block">{time}</span> : null}
            {typeof appointment.jobId === 'string' && appointment.jobId ? (
              <a
                href={`/dashboard/jobs/${encodeURIComponent(appointment.jobId)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
                className="underline underline-offset-2"
              >
                Open job <span className="sr-only">for {title} (opens in a new tab)</span>
              </a>
            ) : null}
          </span>
        );
      })}
    </span>
  );
}

/**
 * What a row IS, independent of where it currently sits in the list.
 *
 * Rows were keyed by their position, which is wrong the moment one of them
 * leaves: every row below shifts up into a key that belonged to its neighbour,
 * inherits that neighbour's exit animation and collapses while it is still
 * true. The identity is the objection itself — its type, whose it is, and the
 * skill or ZIP it names, falling back to the message for a snapshot that
 * records nothing else.
 */
function conflictIdentity(conflict: ScheduleConflict): string {
  const details = conflict.details ?? {};
  const subject = [details.serviceTypeId, details.zipCode]
    .map((value) => (typeof value === 'string' ? value : ''))
    .filter(Boolean)
    .join('|');
  return [conflict.type, conflict.technicianId ?? '', subject || conflict.message].join('·');
}

/**
 * The Busy / Out of area / Missing skill list shared by the inline banner, the
 * "Schedule anyway?" dialog and the read-side "acknowledged issues" note. One
 * row per conflict: severity dot, bucket label, engine message.
 */
export function ConflictList({
  conflicts,
  technicians,
  compact,
  className,
  allowTechnicianUpdates = false,
  onDismiss,
}: ConflictListProps) {
  // Rows on their way out: the fix they offered was applied, or they were
  // dismissed. Either way the refetch behind them removes the row for good, and
  // the mark only has to cover the gap until it lands.
  const [leaving, markLeaving] = useDepartingRows();
  const dismissRow = useCallback(
    (key: string, issues: DismissedIssue[]) => {
      markLeaving(key);
      if (!onDismiss) return;
      // After the collapse, so the refetch it triggers cannot swap the list out
      // from under a row that is still animating.
      window.setTimeout(() => {
        void onDismiss(issues);
      }, COLLAPSE_MS);
    },
    [markLeaving, onDismiss],
  );
  // One stable key per conflict, disambiguated only when a list genuinely
  // repeats the same objection twice.
  const keyOf = useMemo(() => {
    const seen = new Map<string, number>();
    const keys = new Map<ScheduleConflict, string>();
    for (const conflict of conflicts) {
      const identity = conflictIdentity(conflict);
      const seenBefore = seen.get(identity) ?? 0;
      seen.set(identity, seenBefore + 1);
      keys.set(conflict, seenBefore === 0 ? identity : `${identity}#${seenBefore}`);
    }
    return (conflict: ScheduleConflict) => keys.get(conflict) ?? conflictIdentity(conflict);
  }, [conflicts]);
  const nameOf = useMemo(() => {
    const map = new Map((technicians ?? []).map((tech) => [tech.id, tech.name]));
    return (id: string) => map.get(id);
  }, [technicians]);
  const businessHours = conflicts.filter((conflict) => conflict.type === 'OUTSIDE_BUSINESS_HOURS');
  const technicianHours = conflicts.filter((conflict) => conflict.type === 'TECH_OUTSIDE_HOURS');
  const combineHours = businessHours.length > 0 && technicianHours.length > 0;
  const displayedConflicts = combineHours
    ? conflicts.filter(
        (conflict) =>
          conflict.type !== 'OUTSIDE_BUSINESS_HOURS' && conflict.type !== 'TECH_OUTSIDE_HOURS',
      )
    : conflicts;
  const groups = groupConflicts(displayedConflicts, nameOf);
  if (groups.length === 0 && !combineHours) return null;
  // Per-tech headers only make sense when the list spans more than one person
  // AND we can actually name them — an "Technician" placeholder above a message
  // that already names the person is noise (the read-side acknowledged-issues
  // note has the conflicts but not the roster).
  const named = groups.filter((group) => group.technicianName != null);
  const showTechHeaders = named.length > 1 || (named.length > 0 && (technicians?.length ?? 0) > 1);

  return (
    <div className={cn('space-y-2', className)} data-testid="conflict-list">
      {combineHours && (
        <Collapse open={!leaving.has(HOURS_ROW_KEY)}>
          <p className={cn('flex items-start gap-2 leading-snug', compact ? 'text-xs' : 'text-sm')}>
            <span className="min-w-0">
              <span className="font-medium">Outside working hours</span>
              <span className="text-muted-foreground">
                {' — '}
                {[
                  ...new Set(
                    businessHours.map((conflict) => {
                      const hours = conflict.message.match(/\(([^)]+)\)/)?.[1];
                      return hours ? `Business hours: ${hours}` : conflict.message;
                    }),
                  ),
                ].join('; ')}
                . Also outside{' '}
                {[
                  ...new Set(
                    technicianHours.map(
                      (conflict) =>
                        (conflict.technicianId && nameOf(conflict.technicianId)) ||
                        conflict.message.match(/^Outside (.+)'s working hours/)?.[1] ||
                        'the assigned technician',
                    ),
                  ),
                ].join(', ')}
                ’s working hours.
              </span>
            </span>
            {onDismiss ? (
              <DismissIssueButton
                label="Dismiss the outside-working-hours issue"
                onClick={() =>
                  dismissRow(
                    HOURS_ROW_KEY,
                    [...businessHours, ...technicianHours].map(dismissedIssue),
                  )
                }
              />
            ) : null}
          </p>
        </Collapse>
      )}

      {groups.map((group) => {
        const missingSkills = group.buckets
          .flatMap((bucket) => bucket.conflicts)
          .filter(
            (conflict) =>
              conflict.type === 'TECH_SKILL_MISMATCH' &&
              conflict.technicianId &&
              typeof conflict.details?.serviceTypeId === 'string' &&
              typeof conflict.details?.serviceTypeName === 'string',
          );
        const groupedSkills = missingSkills.length > 1 ? missingSkills : [];
        const technicianName =
          group.technicianName ??
          (typeof groupedSkills[0]?.details?.technicianName === 'string'
            ? groupedSkills[0].details.technicianName
            : undefined);
        return (
          <div key={group.technicianId ?? '__all__'} className="space-y-1">
            {(showTechHeaders || groupedSkills.length > 0) && technicianName ? (
              <p className="text-xs font-semibold text-foreground">{technicianName}</p>
            ) : null}
            {groupedSkills.length > 0 && (
              <div className="space-y-1.5 pb-1">
                <p className="text-xs text-muted-foreground">
                  Missing skills ({groupedSkills.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {groupedSkills.map((conflict) => {
                    const chipKey = keyOf(conflict);
                    return (
                      <CollapseInline key={chipKey} open={!leaving.has(chipKey)}>
                        {allowTechnicianUpdates ? (
                          <AddMissingSkillButton
                            compact
                            technicianId={conflict.technicianId!}
                            technicianName={technicianName}
                            serviceTypeId={String(conflict.details!.serviceTypeId)}
                            serviceTypeName={String(conflict.details!.serviceTypeName)}
                            onResolved={() => markLeaving(chipKey)}
                          />
                        ) : (
                          <span className="rounded border border-current/20 px-2 py-1 text-xs">
                            {String(conflict.details!.serviceTypeName)}
                          </span>
                        )}
                      </CollapseInline>
                    );
                  })}
                </div>
              </div>
            )}
            <ul className={cn('space-y-1', compact ? 'text-xs' : 'text-sm')}>
              {group.buckets.flatMap((bucket) =>
                bucket.conflicts
                  .filter((conflict) => !groupedSkills.includes(conflict))
                  .map((conflict) => {
                    const rowKey = keyOf(conflict);
                    return (
                      <li key={rowKey} data-bucket={bucket.key}>
                        <Collapse open={!leaving.has(rowKey)}>
                          <div className="flex items-start gap-2 leading-snug">
                            <span
                              aria-hidden="true"
                              className={cn(
                                'mt-1.5 size-1.5 shrink-0 rounded-full',
                                SEVERITY_DOT_CLASSES[conflict.severity],
                              )}
                            />
                            <span className="min-w-0">
                              <span className="font-medium">
                                {conflict.type === 'WINDOW_CAPACITY_EXCEEDED'
                                  ? 'Arrival-window capacity'
                                  : bucket.label}
                              </span>
                              <span className="text-muted-foreground"> — {conflict.message}</span>
                              <OverlappingAppointments conflict={conflict} />
                              {allowTechnicianUpdates &&
                              conflict.type === 'TECH_SKILL_MISMATCH' &&
                              conflict.technicianId &&
                              typeof conflict.details?.serviceTypeId === 'string' ? (
                                <AddMissingSkillButton
                                  technicianId={conflict.technicianId}
                                  technicianName={
                                    typeof conflict.details?.technicianName === 'string'
                                      ? conflict.details.technicianName
                                      : nameOf(conflict.technicianId)
                                  }
                                  serviceTypeId={conflict.details.serviceTypeId}
                                  serviceTypeName={
                                    typeof conflict.details.serviceTypeName === 'string'
                                      ? conflict.details.serviceTypeName
                                      : undefined
                                  }
                                  onResolved={() => markLeaving(rowKey)}
                                />
                              ) : null}
                              {allowTechnicianUpdates &&
                              conflict.type === 'TECH_TERRITORY_MISMATCH' &&
                              conflict.technicianId &&
                              typeof conflict.details?.zipCode === 'string' &&
                              conflict.details.zipCode ? (
                                <AddServiceAreaButton
                                  technicianId={conflict.technicianId}
                                  technicianName={
                                    typeof conflict.details?.technicianName === 'string'
                                      ? conflict.details.technicianName
                                      : nameOf(conflict.technicianId)
                                  }
                                  zipCode={conflict.details.zipCode}
                                  onResolved={() => markLeaving(rowKey)}
                                />
                              ) : null}
                            </span>
                            {onDismiss ? (
                              <DismissIssueButton
                                label={`Dismiss the ${bucket.label.toLowerCase()} issue`}
                                onClick={() => dismissRow(rowKey, [dismissedIssue(conflict)])}
                              />
                            ) : null}
                          </div>
                        </Collapse>
                      </li>
                    );
                  }),
              )}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
